import { forwardRef } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import styled, { css } from 'styled-components';
import Card from 'components/generic/card';
import { Typography } from 'components/generic/typography';
import PageAction from 'components/shared/Routing/PageAction';
import format from 'helpers/translations';
import { removeButtonStyling, verticalEllipsis } from 'modules/helpers/mixins';
import ThemeProvider from 'theming/ThemeProvider';
import ImageWrapper from './components/ImageWrapper';
import type { ContentCardProps } from './types';

// #region Styles
const imageSize = 92;
const ActionWrapperStyling = css`
    &:after {
        content: '';
        position: absolute;
        inset: 0;
        z-index: 1;
    }

    &:focus-visible {
        outline: none;

        &:after {
            outline: ${({ theme }) => theme.OutlineWidth} solid ${({ theme }) => theme.ColorBorderFocusRing};
            outline-offset: ${({ theme }) => theme.OutlineOffset};
            border-radius: ${({ theme }) => theme.RadiusMedium};
        }
    }

    &:focus:not(:focus-visible) {
        &::after {
            outline: none;
        }
    }
`;

const StyledPageAction = styled(PageAction)`
    ${ActionWrapperStyling}
`;

const ButtonWrapper = styled.button`
    ${removeButtonStyling}
    ${ActionWrapperStyling}
`;

const StyledCard = styled(Card)<{ $showBorder: boolean; $isTransparent?: boolean }>`
    overflow: initial;
    position: relative;
    height: 100%;
    flex-direction: row;
    ${({ $isTransparent }) =>
        $isTransparent
            ? css`
                  background-color: transparent;
              `
            : css`
                  background-color: ${({ theme }) => theme.ColorBackgroundPrimary};
              `}

    ${({ $showBorder, theme }) =>
        $showBorder &&
        css`
            border: 1px solid ${theme.ColorBorderTertiary};
        `};
`;

const StyledImageWrapper = styled(ImageWrapper)<{ $isTransparent?: boolean }>`
    flex: 0 0 ${imageSize}px;
    border-top-left-radius: ${({ theme }) => theme.RadiusMedium};
    border-bottom-left-radius: ${({ theme }) => theme.RadiusMedium};

    ${({ $isTransparent }) =>
        $isTransparent
            ? css`
            border-top-right-radius: ${({ theme }) => theme.RadiusMedium};
            border-bottom-right-radius: ${({ theme }) => theme.RadiusMedium};
        `
            : css`
            border-top-right-radius: 0;
            border-bottom-right-radius: 0;
        `}
`;

const Heading = styled(Typography.Body)`
    margin-bottom: 0;
    color: ${({ theme }) => theme.ColorTextPrimary};
    ${verticalEllipsis(2)}
`;

const Subheading = styled(Typography.Body)`
    margin-bottom: 0;
    color: ${({ theme }) => theme.ColorTextSecondary};
`;

const StyledContentWrapper = styled.div<{ $removeYPadding?: boolean }>`
    display: flex;
    flex-direction: column;
    flex: 1 0 0%;
    gap: ${({ theme }) => theme.Spacing4};

    padding: ${({ theme }) => `${theme.Spacing12} ${theme.Spacing16}`};

    ${({ $removeYPadding }) => {
        if ($removeYPadding) {
            return css`
                padding-top: 0;
                padding-bottom: 0;
            `;
        }
    }}

    a[href]:is(:focus-visible, :active, :hover, :hover:focus) ~ & h3,
    button:is(:focus-visible, :active, :hover, :hover:focus) ~ & h3 {
        text-decoration: underline;
        text-underline-position: from-font;
        text-decoration-skip-ink: none;
    }
`;
// #endregion Styles

const ContentCardHorizontal = forwardRef<HTMLDivElement, ContentCardProps>((props, ref) => {
    const {
        image,
        heading,
        subheading,
        link,
        actions,
        children,
        cardProps,
        onClickWrapper,
        showPlayIcon = false,
        isTransparent = false,
        showBorder = false,
        ...restProps
    } = props;
    const hasOnClickWrapper = onClickWrapper !== undefined;
    const showContentSection = heading || subheading || children || actions || link?.label;
    const imageProps: ContentCardProps['image'] = image
        ? { ...image, aspectRatio: '1/1', sizes: imageSize, transformType: 'SquareScale' }
        : undefined;

    return (
        <StyledCard
            $showBorder={showBorder && !isTransparent}
            $isTransparent={isTransparent}
            forwardedAs="article"
            ref={ref}
            {...restProps}
            {...cardProps}
        >
            {link?.action && !hasOnClickWrapper && (
                <StyledPageAction action={link.action} aria-label={link.label || heading} />
            )}
            {hasOnClickWrapper && <ButtonWrapper onClick={onClickWrapper} />}
            <StyledImageWrapper
                image={imageProps}
                horizontal
                showPlayIcon={showPlayIcon}
                $isTransparent={isTransparent}
            />
            {showContentSection && (
                <StyledContentWrapper $removeYPadding={isTransparent}>
                    {subheading && <Subheading size="S">{format(subheading)}</Subheading>}
                    {heading && (
                        <Heading size="M" strong>
                            {format(heading)}
                        </Heading>
                    )}
                </StyledContentWrapper>
            )}
        </StyledCard>
    );
});

const ContentCardThemeWrapper = forwardRef<HTMLDivElement, ContentCardProps>((props, ref) => {
    const { type, isTransparent = false } = props;
    const theme = isTransparent ? 'default' : type;
    const showBorder = props.showBorder !== undefined ? props.showBorder : theme === 'default';

    return theme ? (
        <ThemeProvider theme={theme}>
            <ContentCardHorizontal ref={ref} {...props} showBorder={showBorder} />
        </ThemeProvider>
    ) : (
        // if no type is provided we will use the global theme
        <ContentCardHorizontal ref={ref} {...props} showBorder={showBorder} />
    );
});

export default withErrorBoundary(ContentCardThemeWrapper, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
